/* eslint-disable react-hooks/exhaustive-deps */
import React, {
    useEffect,
    useRef,
    useState
} from "react";
import {FileUpload} from "primereact/fileupload";
import moment from "moment";
import useApiViaje from "../../../api/operaciones/useApiViajes";
import useServiceFactory from "../../../api/serviceFactory";
import alerts from "../../../shared/alerts";
import FieldScanner from "../../../shared/controls/fieldScanner";

const ArchivosCi = ({
    viajeId,
    soloLectura = false
}) => {
    const api = useApiViaje();

    const service = useServiceFactory({
        uri: process.env.REACT_APP_API_URL
    });

    const fileUploadRef = useRef(null);

    const [archivos, setArchivos] =
        useState([]);

    const [guardando, setGuardando] =
        useState(false);

    useEffect(() => {
        cargar();
    }, [viajeId]);

    const cargar = async () => {
        if (!(viajeId > 0)) {
            setArchivos([]);
            return;
        }

        const resultado =
            await api.obtenerArchivosCi(
                viajeId
            );

        setArchivos(resultado ?? []);
    };

    const obtenerUrl = resultado => {
        if (typeof resultado === "string") {
            return resultado;
        }

        return (
            resultado?.url ??
            resultado?.archivo?.url ??
            resultado?.value?.url ??
            resultado?.value
        );
    };

    const obtenerNombre = url => {
        if (!url) {
            return "Archivo CI";
        }

        const rutaSinParametros =
            url.split("?")[0];

        return decodeURIComponent(
            rutaSinParametros
                .split("/")
                .pop()
        );
    };

    const obtenerExtension = url => {
        if (!url) {
            return "";
        }

        const rutaSinParametros =
            url.split("?")[0];

        const extension =
            rutaSinParametros
                .split(".")
                .pop()
                .toLowerCase();

        // FieldScanner reconoce JPG.
        return extension === "jpeg"
            ? "jpg"
            : extension;
    };

    const crearArchivoDto = item => ({
        url: item.url,
        nombre: obtenerNombre(item.url),
        extension: obtenerExtension(
            item.url
        ),
        isEmpty: !item.url
    });

    const eliminar = async item => {
        await api.eliminarArchivoCi(
            viajeId,
            item.idViajeArchivoCI
        );

        await cargar();
    };

    const subir = async event => {
        const files = Array.from(
            event.files ?? []
        );

        /*
         * Se mantiene multiple=true por el problema
         * de arrastre de PrimeReact 7.2.1, pero
         * solamente permitimos procesar uno.
         */
        if (files.length !== 1) {
            fileUploadRef.current?.clear();

            await alerts.aviso(
                "Solamente puede cargar un archivo CI a la vez."
            );

            return;
        }

        const file = files[0];
        const maxFileSize =
            10 * 1024 * 1024;

        if (file.size > maxFileSize) {
            fileUploadRef.current?.clear();

            await alerts.aviso(
                "El archivo no puede superar los 10 MB."
            );

            return;
        }

        try {
            setGuardando(true);

            const formData =
                new FormData();

            formData.append(
                "file",
                file
            );

            /*
             * Guarda el archivo utilizando el
             * mecanismo existente de almacenamiento.
             */
            const resultadoArchivo =
                await service.post(
                    "/archivos/subir/Viajes_CI",
                    formData
                );

            const urlArchivo =
                obtenerUrl(
                    resultadoArchivo
                );

            if (!urlArchivo) {
                await alerts.aviso(
                    "El servidor no devolvió la URL del archivo."
                );

                return;
            }

            /*
             * Guarda la URL y la información
             * de auditoría en OPE_ViajeArchivoCI.
             */
            await api.guardarUrlCi(
                viajeId,
                urlArchivo
            );

            fileUploadRef.current?.clear();

            await cargar();
        } finally {
            setGuardando(false);
        }
    };

    return (
        <div>
            {!soloLectura && (
                <div className="mb-3">
                    <FileUpload
                        ref={fileUploadRef}
                        name="file"
                        mode="advanced"
                        customUpload
                        auto
                        multiple={true}
                        accept={
                            "application/pdf," +
                            "image/png," +
                            "image/jpeg"
                        }
                        maxFileSize={
                            10 * 1024 * 1024
                        }
                        disabled={guardando}
                        uploadHandler={subir}
                        chooseLabel={
                            "Seleccionar archivo"
                        }
                        uploadLabel="Guardar"
                        cancelLabel="Cancelar"
                        emptyTemplate={
                            <div
                                className={
                                    "text-center p-4"
                                }
                            >
                                <i
                                    className={
                                        "pi pi-cloud-upload"
                                    }
                                    style={{
                                        fontSize:
                                            "3rem",
                                        color:
                                            "#6c757d"
                                    }}
                                />

                                <p
                                    className={
                                        "mt-3 mb-0"
                                    }
                                >
                                    Arrastra aquí el
                                    archivo CI o haz
                                    clic en “Seleccionar
                                    archivo”.
                                </p>

                                <small>
                                    PDF, PNG o JPG.
                                    Tamaño máximo:
                                    10 MB.
                                </small>
                            </div>
                        }
                    />
                </div>
            )}

            <table className="table table-sm">
                <thead className="thead-light">
                    <tr>
                        <th>
                            ARCHIVO CI
                        </th>

                        <th
                            style={{
                                width: "20%"
                            }}
                        >
                            FECHA Y HORA
                        </th>

                        <th
                            style={{
                                width: "20%"
                            }}
                        >
                            USUARIO
                        </th>

                        <th
                            style={{
                                width: "12%"
                            }}
                        >
                            ACCIONES
                        </th>
                    </tr>
                </thead>

                <tbody>
                    {archivos.map(item => (
                        <tr
                            key={
                                item
                                    .idViajeArchivoCI
                            }
                        >
                            <td>
                                {obtenerNombre(
                                    item.url
                                )}
                            </td>

                            <td>
                                {moment(
                                    item.fechaHora
                                ).format(
                                    "DD/MM/YYYY HH:mm"
                                )}
                            </td>

                            <td>
                                {item.auditUser}
                            </td>

                            <td>
                                <FieldScanner
                                    name={
                                        `archivoCi-${
                                            item
                                                .idViajeArchivoCI
                                        }`
                                    }
                                    value={
                                        crearArchivoDto(
                                            item
                                        )
                                    }
                                    disabled={
                                        soloLectura
                                    }
                                    visibleEscanear={
                                        false
                                    }
                                    onEliminar={() =>
                                        eliminar(
                                            item
                                        )
                                    }
                                />
                            </td>
                        </tr>
                    ))}

                    {archivos.length === 0 && (
                        <tr>
                            <td
                                colSpan={4}
                                className={
                                    "text-center"
                                }
                            >
                                No hay archivos CI
                                registrados.
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    );
};

export default ArchivosCi;